import React, { useEffect, useRef } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import LogoIcon from '../components/LogoIcon';

// ── Particle canvas ──────────────────────────────────────────────────────────
const ParticleCanvas: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let animId: number;

    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener('resize', resize);

    const particles = Array.from({ length: 60 }, () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      r: Math.random() * 1.8 + 0.4,
      dx: (Math.random() - 0.5) * 0.3,
      dy: (Math.random() - 0.5) * 0.3,
      alpha: Math.random() * 0.4 + 0.08,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const p of particles) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(192,10,26,${p.alpha})`;
        ctx.fill();
        p.x += p.dx; p.y += p.dy;
        if (p.x < 0 || p.x > canvas.width)  p.dx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.dy *= -1;
      }
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
  }, []);

  return <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }} />;
};

// ── Keyframes ────────────────────────────────────────────────────────────────
const STYLES = `
@keyframes fadeUp {
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes logoPulse {
  0%, 100% { filter: drop-shadow(0 0 18px rgba(192,10,26,0.55)); }
  50%       { filter: drop-shadow(0 0 40px rgba(192,10,26,0.95)); }
}
@keyframes orb1 {
  0%, 100% { transform: translate(0,0) scale(1); }
  50%       { transform: translate(60px,-40px) scale(1.15); }
}
@keyframes orb2 {
  0%, 100% { transform: translate(0,0) scale(1); }
  50%       { transform: translate(-50px,55px) scale(1.1); }
}
@keyframes barFill {
  from { width: 0%; }
  to   { width: var(--bar-width); }
}
@keyframes countUp {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
`;

// ── Stat pill ────────────────────────────────────────────────────────────────
const Stat: React.FC<{ value: string; label: string; delay: string }> = ({ value, label, delay }) => (
  <Box sx={{
    textAlign: 'center', px: 3,
    animation: `countUp 0.6s ease both`,
    animationDelay: delay,
  }}>
    <Typography sx={{
      fontSize: '2rem', fontWeight: 800, lineHeight: 1,
      background: 'linear-gradient(135deg, #FF1A50 0%, #ff6b8a 100%)',
      WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
      mb: 0.5,
    }}>
      {value}
    </Typography>
    <Typography sx={{ fontSize: '0.72rem', color: '#6b7280', fontWeight: 500, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
      {label}
    </Typography>
  </Box>
);

// ── App usage card ───────────────────────────────────────────────────────────
interface AppBarProps {
  name: string;
  department: string;
  inputPct: number;
  outputPct: number;
  inputLabel: string;
  outputLabel: string;
  model: string;
  delay: string;
}
const AppBar: React.FC<AppBarProps> = ({ name, department, inputPct, outputPct, inputLabel, outputLabel, model, delay }) => (
  <Box sx={{
    p: 2.5,
    bgcolor: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: '12px',
    backdropFilter: 'blur(12px)',
    animation: `fadeUp 0.6s ease both`,
    animationDelay: delay,
    transition: 'border-color 0.2s, background 0.2s',
    '&:hover': { bgcolor: 'rgba(192,10,26,0.06)', borderColor: 'rgba(192,10,26,0.3)' },
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.75 }}>
      <Box>
        <Typography sx={{ fontWeight: 600, fontSize: '0.88rem', color: '#f1f5f9', mb: 0.2 }}>{name}</Typography>
        <Typography sx={{ fontSize: '0.72rem', color: '#6b7280' }}>{department}</Typography>
      </Box>
      <Box sx={{
        px: 1.25, py: 0.35, borderRadius: '5px',
        bgcolor: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.25)',
      }}>
        <Typography sx={{ fontSize: '0.67rem', fontWeight: 600, color: '#60a5fa', fontFamily: 'monospace' }}>
          {model}
        </Typography>
      </Box>
    </Box>

    {/* Input tokens bar */}
    <Box sx={{ mb: 1.25 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography sx={{ fontSize: '0.68rem', color: '#6b7280' }}>Input tokens</Typography>
        <Typography sx={{ fontSize: '0.68rem', color: '#22d3ee', fontWeight: 600 }}>{inputLabel}</Typography>
      </Box>
      <Box sx={{ height: 5, bgcolor: 'rgba(255,255,255,0.07)', borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{
          height: '100%', width: `${inputPct}%`, borderRadius: 3,
          background: 'linear-gradient(90deg, #22d3ee, #0ea5e9)',
          transition: 'width 1.2s cubic-bezier(0.22,1,0.36,1)',
        }} />
      </Box>
    </Box>

    {/* Output tokens bar */}
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography sx={{ fontSize: '0.68rem', color: '#6b7280' }}>Output tokens</Typography>
        <Typography sx={{ fontSize: '0.68rem', color: '#a78bfa', fontWeight: 600 }}>{outputLabel}</Typography>
      </Box>
      <Box sx={{ height: 5, bgcolor: 'rgba(255,255,255,0.07)', borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{
          height: '100%', width: `${outputPct}%`, borderRadius: 3,
          background: 'linear-gradient(90deg, #a78bfa, #7c3aed)',
          transition: 'width 1.2s cubic-bezier(0.22,1,0.36,1)',
        }} />
      </Box>
    </Box>
  </Box>
);

// ── Feature card ─────────────────────────────────────────────────────────────
const FeatureCard: React.FC<{ icon: string; title: string; desc: string; delay: string }> = ({ icon, title, desc, delay }) => (
  <Box sx={{
    flex: '1 1 180px', minWidth: 160, maxWidth: 240,
    px: 2.5, py: 2.5,
    bgcolor: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: '12px',
    backdropFilter: 'blur(12px)',
    animation: `fadeUp 0.7s ease both`,
    animationDelay: delay,
    transition: 'all 0.2s',
    '&:hover': { bgcolor: 'rgba(192,10,26,0.07)', borderColor: 'rgba(192,10,26,0.35)', transform: 'translateY(-2px)' },
  }}>
    <Typography sx={{ fontSize: '1.5rem', mb: 1 }}>{icon}</Typography>
    <Typography sx={{ color: '#f1f5f9', fontWeight: 600, fontSize: '0.88rem', mb: 0.5 }}>{title}</Typography>
    <Typography sx={{ color: '#6b7280', fontSize: '0.78rem', lineHeight: 1.6 }}>{desc}</Typography>
  </Box>
);

// ── Demo apps data ────────────────────────────────────────────────────────────
const DEMO_APPS: AppBarProps[] = [
  { name: 'Customer Support Bot', department: 'Customer Success',  inputPct: 78, outputPct: 62, inputLabel: '78K / 100K',  outputLabel: '31K / 50K',  model: 'gpt-4o',      delay: '0.55s' },
  { name: 'Document Analyzer',    department: 'Legal & Compliance', inputPct: 45, outputPct: 38, inputLabel: '45K / 100K',  outputLabel: '19K / 50K',  model: 'gpt-4o-mini', delay: '0.65s' },
  { name: 'Code Assistant',       department: 'Engineering',        inputPct: 92, outputPct: 84, inputLabel: '92K / 100K',  outputLabel: '42K / 50K',  model: 'gpt-4o',      delay: '0.75s' },
];

// ── Page ─────────────────────────────────────────────────────────────────────
const HomePage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <>
      <style>{STYLES}</style>
      <Box sx={{
        minHeight: '100vh', bgcolor: '#0d0d1a',
        position: 'relative', overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
      }}>
        <ParticleCanvas />

        {/* Gradient orbs */}
        <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
          <Box sx={{ position: 'absolute', top: '5%', left: '5%', width: 500, height: 500, background: 'radial-gradient(circle, rgba(192,10,26,0.15) 0%, transparent 70%)', borderRadius: '50%', animation: 'orb1 11s ease-in-out infinite' }} />
          <Box sx={{ position: 'absolute', bottom: '5%', right: '4%', width: 560, height: 560, background: 'radial-gradient(circle, rgba(177,43,53,0.1) 0%, transparent 70%)', borderRadius: '50%', animation: 'orb2 14s ease-in-out infinite' }} />
          <Box sx={{ position: 'absolute', top: '40%', right: '18%', width: 320, height: 320, background: 'radial-gradient(circle, rgba(80,10,100,0.1) 0%, transparent 70%)', borderRadius: '50%', animation: 'orb1 18s ease-in-out infinite reverse' }} />
        </Box>

        {/* ── Top nav ───────────────────────────────────────────────── */}
        <Box sx={{
          position: 'relative', zIndex: 10,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: { xs: 3, sm: 5 }, pt: 3,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <LogoIcon size={22} letterColor="#ffffff" />
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#f1f5f9', letterSpacing: '-0.02em' }}>
              LLM Wrapper
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              onClick={() => navigate('/docs')}
              sx={{
                px: 2, py: 0.75, borderRadius: '7px', cursor: 'pointer',
                border: '1px solid rgba(255,255,255,0.12)', fontSize: '0.8rem',
                fontWeight: 500, color: '#9ca3af',
                '&:hover': { color: '#f1f5f9', borderColor: 'rgba(255,255,255,0.25)' },
                transition: 'all 0.15s',
              }}
            >
              API Docs
            </Box>
            <Button
              size="small"
              onClick={() => navigate('/login')}
              sx={{
                bgcolor: '#b12b35', color: '#fff', fontWeight: 600, fontSize: '0.8rem',
                px: 2.25, py: 0.75, borderRadius: '7px', textTransform: 'none',
                '&:hover': { bgcolor: '#c0392b' }, transition: 'background 0.15s',
              }}
            >
              Sign In
            </Button>
          </Box>
        </Box>

        {/* ── Hero ──────────────────────────────────────────────────── */}
        <Box sx={{
          position: 'relative', zIndex: 1, flex: 1,
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          pt: { xs: 6, sm: 8 }, pb: 6, px: 3,
        }}>

          {/* Logo */}
          <Box sx={{ mb: 3.5, animation: 'logoPulse 3.5s ease-in-out infinite' }}>
            <LogoIcon size={72} letterColor="#ffffff" />
          </Box>

          {/* Headline */}
          <Box sx={{ animation: 'fadeUp 0.6s ease both', animationDelay: '0.1s', textAlign: 'center', mb: 2 }}>
            <Typography sx={{
              fontSize: { xs: '2.2rem', sm: '3rem' }, fontWeight: 800,
              lineHeight: 1.1, letterSpacing: '-0.03em',
              background: 'linear-gradient(135deg, #ffffff 0%, #e5e7eb 50%, #9ca3af 100%)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
              mb: 0.5,
            }}>
              LLM Wrapper
            </Typography>
            <Typography sx={{
              fontSize: '0.82rem', fontWeight: 500, color: '#6b7280',
              letterSpacing: '0.18em', textTransform: 'uppercase',
            }}>
              Enterprise AI Gateway
            </Typography>
          </Box>

          {/* Sub-copy */}
          <Box sx={{ animation: 'fadeUp 0.6s ease both', animationDelay: '0.22s', textAlign: 'center', mb: 4 }}>
            <Typography sx={{ color: '#9ca3af', fontSize: '1rem', lineHeight: 1.75, maxWidth: 500, mx: 'auto' }}>
              Centrally manage AI apps, token quotas, endpoint permissions, and org-wide OpenAI access — with zero changes to your existing code.
            </Typography>
          </Box>

          {/* CTA */}
          <Box sx={{
            display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap',
            animation: 'fadeUp 0.6s ease both', animationDelay: '0.35s', mb: 6,
          }}>
            <Button
              variant="contained" size="large"
              onClick={() => navigate('/')}
              sx={{
                bgcolor: '#b12b35', color: '#fff', fontWeight: 700, fontSize: '0.9rem',
                px: 3.5, py: 1.25, borderRadius: '8px', textTransform: 'none',
                boxShadow: '0 4px 24px rgba(192,10,26,0.4)',
                '&:hover': { bgcolor: '#c0392b', boxShadow: '0 6px 32px rgba(192,10,26,0.6)', transform: 'translateY(-1px)' },
                transition: 'all 0.2s',
              }}
            >
              Open Dashboard
            </Button>
            <Button
              variant="outlined" size="large"
              onClick={() => navigate('/docs')}
              sx={{
                borderColor: 'rgba(255,255,255,0.18)', color: '#e5e7eb', fontWeight: 500,
                fontSize: '0.9rem', px: 3.5, py: 1.25, borderRadius: '8px', textTransform: 'none',
                backdropFilter: 'blur(8px)',
                '&:hover': { borderColor: 'rgba(192,10,26,0.5)', bgcolor: 'rgba(192,10,26,0.08)' },
                transition: 'all 0.2s',
              }}
            >
              API Docs
            </Button>
          </Box>

          {/* ── Stats row ───────────────────────────────────────────── */}
          <Box sx={{
            display: 'flex', gap: 0, flexWrap: 'wrap', justifyContent: 'center',
            animation: 'fadeUp 0.6s ease both', animationDelay: '0.45s', mb: 7,
            border: '1px solid rgba(255,255,255,0.07)',
            borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.03)',
            backdropFilter: 'blur(12px)', px: 2,
          }}>
            {[
              { value: '150+',    label: 'AI Models',         delay: '0.5s' },
              { value: '100M+', label: 'Tokens / Day',      delay: '0.6s' },
              { value: '99.9%', label: 'Uptime',            delay: '0.7s' },
              { value: '0',     label: 'Code Changes',      delay: '0.8s' },
            ].map(({ value, label, delay }, i, arr) => (
              <Box key={label} sx={{
                display: 'flex', alignItems: 'center',
                borderRight: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.07)' : 'none',
              }}>
                <Stat value={value} label={label} delay={delay} />
              </Box>
            ))}
          </Box>

          {/* ── App token bars ──────────────────────────────────────── */}
          <Box sx={{
            width: '100%', maxWidth: 780,
            animation: 'fadeUp 0.6s ease both', animationDelay: '0.5s', mb: 5,
          }}>
            <Typography sx={{
              fontSize: '0.72rem', fontWeight: 600, color: '#6b7280',
              letterSpacing: '0.08em', textTransform: 'uppercase', mb: 2, textAlign: 'center',
            }}>
              Live Application Token Usage
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
              {DEMO_APPS.map(app => <AppBar key={app.name} {...app} />)}
            </Box>
          </Box>

          {/* ── Feature cards ───────────────────────────────────────── */}
          <Box sx={{
            display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap',
            maxWidth: 860, width: '100%',
          }}>
            <FeatureCard icon="🔑" title="API Key Management"  desc="Securely store and rotate OpenAI keys with per-app encryption."    delay="0.6s"  />
            <FeatureCard icon="⚡" title="Token Quotas"        desc="Daily input & output limits per app and per model, auto-reset."    delay="0.7s"  />
            <FeatureCard icon="🔒" title="Endpoint Control"    desc="Grant granular access to completions, embeddings, audio & more."   delay="0.8s"  />
            <FeatureCard icon="📊" title="Usage Analytics"     desc="Real-time token usage dashboards with per-app and model breakdown." delay="0.9s"  />
            <FeatureCard icon="🤖" title="Agent Ready"         desc="Drop-in compatible with LangChain, AutoGen, CrewAI, LlamaIndex."   delay="1.0s"  />
            <FeatureCard icon="🛡️" title="Microsoft SSO"       desc="Role-based access via Azure AD — Super Admin, Admin, Viewer."     delay="1.1s"  />
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{
          position: 'relative', zIndex: 1, textAlign: 'center',
          pb: 3, animation: 'fadeUp 0.6s ease both', animationDelay: '1.1s',
        }}>
          <Typography sx={{ color: '#374151', fontSize: '0.72rem' }}>
            © {new Date().getFullYear()} TestingXperts · LLM Wrapper v2
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default HomePage;
